import { useDispatch, useSelector } from 'react-redux';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { Nullable } from 'shared/types';
import { ViewParams } from 'shared/types/view-params';
import { configSelector } from 'store/config/selectors';
import { saveExternalConfig } from 'store/config/actions';
import { useSearchParams } from 'hooks/use-search-params.hook';
import { extractBuildingUuidFromPath } from 'utils/formatPath';

export const useViewParams = (): Nullable<Partial<ViewParams>> => {
  const searchParams = useSearchParams<Partial<ViewParams>>();
  const config = useSelector(configSelector);
  const dispatch = useDispatch();
  const { pathname } = useLocation();

  // Extract buildingUuid from path if present
  const pathBuildingUuid = useMemo(() => extractBuildingUuidFromPath(pathname), [pathname]);

  useEffect(() => {
    // Combine search params with path-based buildingUuid (path takes precedence)
    const combinedParams = {
      ...searchParams,
      ...(pathBuildingUuid ? { buildingUuid: pathBuildingUuid } : {}),
    };

    if (Object.keys(combinedParams).length > 0) {
      dispatch(saveExternalConfig(combinedParams));
    }
  }, [dispatch, searchParams, pathBuildingUuid]);

  return useMemo(
    () => ({
      ...config,
      ...searchParams,
      ...(pathBuildingUuid ? { buildingUuid: pathBuildingUuid } : {}),
    }),
    [searchParams, config, pathBuildingUuid],
  );
};
